import { useState, useEffect, useCallback, useRef } from "react";
import { loadApiSetting, ApiNotConfiguredNotice, fetchViaProxy } from "./apiSettingsHelpers";
import DraggableModal from "./DraggableModal";
import SearchSelect from "./SearchSelect";
import EmailDialog from "./EmailDialog";
import { loadCompanyProfile } from "./CompanyProfile";
import { isFourSeasonsClientName } from "./emailHelpers";
import { fetchGoogleNewsRss } from "./MarketNewsRegistry";
import { loadFromSupabase } from "../supabaseDB";

/* GFI Nexus — Weather Watch (fix-dashboard.md "Fase C.1")
   ─────────────────────────────────────────────────────────────────
   NOAA is the spec's "primary" source in CLAUDE.md's roadmap doc, but
   OpenWeather is what CAMBIO explicitly wires up here (simpler auth —
   a single `appid` query param vs NOAA's token+dataset-discovery
   dance — and it's the one with a free-tier 5-day forecast endpoint
   this screen actually needs). NOAA anomaly cross-checking is a Fase
   D-era refinement, not this pass. */

/* 2026-10-13, Arturo: "las regiones que afectan mi produccion son
   michoacan, jalisco y guerrero" — California/Florida/Georgia (US
   growing regions, never actually tied to GFI's own Mexican supply)
   replaced with Guerrero. Guerrero's own coordinate is the state
   capital (Chilpancingo), same state-level precision already used for
   Jalisco — can be narrowed to a specific town later if Arturo wants
   the same per-town precision Michoacán already has. */
var REGIONS = [
  { key: "michoacan", nameEn: "Los Reyes de Salgado, Michoacán", nameEs: "Los Reyes de Salgado, Michoacán", lat: 19.5865, lon: -102.4784, noteEn: "Main berry production area", noteEs: "Principal zona de producción de berries" },
  { key: "jalisco", nameEn: "Jalisco, Mexico", nameEs: "Jalisco, México", lat: 20.6595, lon: -103.3496 },
  { key: "guerrero", nameEn: "Guerrero, Mexico", nameEs: "Guerrero, México", lat: 17.5506, lon: -99.5024 },
];

var AUTO_REFRESH_MS = 30 * 60 * 1000;
var HURRICANE_REFRESH_MS = 30 * 60 * 1000;
/* Reference point for the "distance to X" risk calc — same coords as
   the Michoacán region above (Los Reyes de Salgado, main berry
   production area). Kept as its own constant rather than reading
   REGIONS[0] so this doesn't silently break if REGIONS gets reordered. */
var LOS_REYES = { lat: 19.5865, lon: -102.4784 };

var T = {
  en: {
    title: "Weather Watch", subtitle: "Live conditions and anomaly alerts for the producing regions.",
    refresh: "Refresh", refreshing: "Refreshing…", retry: "Retry",
    feelsLike: "Feels like", humidity: "Humidity", wind: "Wind",
    forecast5: "5-Day Forecast", updated: "Updated",
    frostRisk: "⚠ Frost risk", strongWind: "⚠ Strong winds", heavyRain: "⚠ Heavy rain expected",
    loadError: "Could not load weather data.", notConfigured: "OpenWeather API",
    hurricaneTitle: "Hurricane Alerts — Mexican Pacific", noActive: "No active hurricane threats in Mexican Pacific",
    maxWinds: "Max winds", movement: "Movement", distanceTo: "Distance to Los Reyes de Salgado",
    hurricaneLoadError: "Could not load NHC hurricane data.",
    sendAlert: "Send Weather Alert", alertAudience: "Audience", alertShippers: "Shippers", alertClients: "Clients", alertFourSeasons: "Four Seasons",
    alertCompanies: "Companies", alertCompaniesHint: "Pick which companies receive this alert.", alertContinue: "Continue", alertCancel: "Cancel",
    alertNoCompanies: "Select at least one company.", alertBuilding: "Building alert…",
    alertSubject: "Weather Advisory — Berry Production Regions",
  },
  es: {
    title: "Monitor de Clima", subtitle: "Condiciones en vivo y alertas de anomalías para las regiones productoras.",
    refresh: "Actualizar", refreshing: "Actualizando…", retry: "Reintentar",
    feelsLike: "Sensación", humidity: "Humedad", wind: "Viento",
    forecast5: "Pronóstico 5 Días", updated: "Actualizado",
    frostRisk: "⚠ Riesgo de helada", strongWind: "⚠ Vientos fuertes", heavyRain: "⚠ Lluvia intensa esperada",
    loadError: "No se pudo cargar el clima.", notConfigured: "OpenWeather API",
    hurricaneTitle: "Alertas de Huracanes — Pacífico Mexicano", noActive: "Sin amenazas de huracanes activas en el Pacífico Mexicano",
    maxWinds: "Vientos máximos", movement: "Movimiento", distanceTo: "Distancia a Los Reyes de Salgado",
    hurricaneLoadError: "No se pudo cargar la información de huracanes del NHC.",
    sendAlert: "Enviar Alerta de Clima", alertAudience: "Destinatarios", alertShippers: "Shippers", alertClients: "Clientes", alertFourSeasons: "Four Seasons",
    alertCompanies: "Empresas", alertCompaniesHint: "Elige qué empresas reciben esta alerta.", alertContinue: "Continuar", alertCancel: "Cancelar",
    alertNoCompanies: "Selecciona al menos una empresa.", alertBuilding: "Generando alerta…",
    alertSubject: "Aviso Climático y de Mercado — Regiones de Producción de Berries",
  },
};

var ALERT_LABEL_KEY = { frost: "frostRisk", wind: "strongWind", rain: "heavyRain" };

/* ======================== Send Weather Alert ========================
   2026-10-13, Arturo: "quisiera poder enviar un weather watch alert a
   clientes y shippers, a shippers en español, a clientes en ingles,
   si envio a four seasons enviar desde el masked email de sweetleaf,
   a otros clientes desde ground-fresh masked email". Confirmed
   explicitly (AskUserQuestion) that this ONE feature is allowed to
   switch From-account by recipient from a single screen — a
   deliberate, scoped exception to REGLA DE ENVÍO DE EMAILS (every
   other email in this app routes Four Seasons through SweetLeafPortal.js
   only). Same per-file duplication convention as everywhere else —
   getClients/getSuppliers/loadLS are plain localStorage readers,
   duplicated here rather than imported from another Registry. */
function loadLS(key, fallback) {
  try { var raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : fallback; } catch (e) { return fallback; }
}
function getClients() { return loadLS("gfi-clients", []); }
function getSuppliers() { return loadLS("gfi-suppliers", []); }

/* Same "never trust the localStorage mirror for a one-off send" rule
   as every other getFreshSlSettings()/ensureCompanyProfileFresh()
   duplicate in this app — reads sweetleaf_settings fresh so the
   masked account is always current, since WeatherWatchRegistry.js
   never otherwise touches this table.
   2026-10-12, Arturo: "quiero que para cada uno salga de las cuentas
   de alertas... sweetleaf de Sweetleaf-Alerts@gfinexus.com" — prefer
   the dedicated, name-free "SL Automated" alias
   (protectionReminderMaskedEmailAccount, same field the automated
   Protection Status Reminder job already uses) over Arturo's own
   personal SweetLeaf masked account; falls back to the regular
   maskedEmailAccount only if "SL Automated" hasn't been configured in
   Settings yet, same safety net as every other automated-account
   resolution in this app. */
async function getFreshSlAccount() {
  try {
    var rows = await loadFromSupabase("sweetleaf_settings", { force: true });
    var row = (rows || [])[0] || {};
    var maskedFrom = row.protectionReminderMaskedEmailAccount || row.maskedEmailAccount || "";
    return { maskedFrom: maskedFrom, email: row.emailAccount || "", signature: row.emailSignature || "" };
  } catch (e) { return { maskedFrom: "", email: "", signature: "" }; }
}

/* 2026-10-13, Arturo, real production incident: "te dije claramente
   que el email de four seasons tendria que salir de la cuenta
   Sweetleaf-Alerts@gfinexus.com y salio de mi cuenta personal... y el
   de ground fresh deberia salir de la cuenta alerts@gfinexus.com y
   salio de la mia personal" — root cause found in EmailDialog.js
   (documented there, 2026-09-14 bug note): handleSend does NOT simply
   trust the `emailAccounts` prop at send time — unless the caller
   passes `refreshAccountsBeforeSend`, it refetches company_profile
   itself and re-matches by `.email`. WeatherWatchRegistry.js never
   passed that prop, so at the moment of actually sending:
   - The SweetLeaf (Four Seasons) branch's accounts come from
     sweetleaf_settings, not company_profile — EmailDialog's default
     refetch searched the WRONG table entirely, found no match, and
     fell through to Arturo's raw real address with no mask at all.
   - The Ground-Fresh branch's "GFI Automated" account shares its real
     `.email` ("a.gonzalez@ground-fresh.com") with the separate
     "Ground-Fresh" personal account in company_profile — `.find()`
     matching by email alone returns whichever one happens to come
     first in that array (the personal one), silently overriding the
     automated account this screen carefully resolved for display.
   These two functions are the single source of truth for both the
   dialog's initial display AND the real send — passed again as
   `refreshAccountsBeforeSend` below so EmailDialog's own refetch-and-
   maybe-mismatch logic never runs at all for this feature. */
async function resolveSlAlertAccounts() {
  var slAcct = await getFreshSlAccount();
  return slAcct.maskedFrom ? [{ name: "SL Automated", email: slAcct.email, maskedFrom: slAcct.maskedFrom }] : [];
}
async function resolveGfAlertAccounts() {
  await loadFromSupabase("company_profile").catch(function () {});
  var company = loadCompanyProfile();
  var gfAcct = (company.emailAccounts || []).find(function (a) { return String((a && a.maskedFrom) || "").toLowerCase() === "alerts@gfinexus.com"; })
    || (company.emailAccounts || []).find(function (a) { return (a.name || "").toLowerCase().indexOf("automat") >= 0; })
    || (company.emailAccounts || []).find(function (a) { return /ground.?fresh/i.test(a.name || ""); })
    || (company.emailAccounts || []).filter(function (a) { return (a.name || "").toLowerCase().indexOf("sweetleaf") < 0; })[0] || null;
  return gfAcct ? [gfAcct] : [];
}

var ALERT_NEWS_KEYWORDS = ["hurricane Mexico Pacific", "Michoacan weather", "berry Mexico harvest"];

/* 2026-10-12, Arturo: "has referencia que este huracan ya esta
   generando lluvias, con la intensidad del forecast, si dice ligera,
   pues ligeras, si dice intensas pues intensas, debe ser de acuerdo a
   la informacion obtenida del clima" — classifies the real next-24h
   rain total (mm, from the same OpenWeather forecast already driving
   the "rain" badge) into a wording band, rather than a single
   hardcoded "intensa"/"heavy" regardless of how much rain the
   forecast actually shows. Standard light/moderate/heavy 24h rainfall
   bands (not a figure Arturo gave — this app's own "rain" alert only
   fires above 50mm to begin with, so most real alerts will land in
   the heavy band, but this keeps the wording honest if that gate
   value ever changes). */
function rainIntensityWord(mm, lang) {
  var n = typeof mm === "number" ? mm : 0;
  if (lang === "es") return n >= 30 ? "intensas" : (n >= 10 ? "moderadas" : "ligeras");
  return n >= 30 ? "heavy" : (n >= 10 ? "moderate" : "light");
}

/* Best-effort — a weather alert is still worth sending without news
   links if the fetch fails or returns nothing. */
async function fetchAlertNews() {
  try {
    var items = await fetchGoogleNewsRss(ALERT_NEWS_KEYWORDS, "7d");
    return (items || []).filter(function (i) { return i && i.title && i.url; }).slice(0, 3);
  } catch (e) { return []; }
}

/* Branded HTML block inserted before the signature (same
   insertBeforeSignatureHtml pattern Customer Remarks/Quality Remarks
   already use) — a self-contained advisory with real weather icon
   images (OpenWeather's own public icon URLs, already used on-screen
   in RegionCard) and, when available, 2-3 real linked headlines.
   `brand` picks GFI navy/gold vs SweetLeaf teal/mint, same palettes
   CLAUDE.md's own "EMAIL NOTIFICATIONS" section already documents for
   Inbound/Outbound templates — reused here rather than inventing a
   3rd look for a brand-new document type. */
function buildWeatherAlertHtml(opts) {
  var lang = opts.lang, brand = opts.brand, regionData = opts.regionData, hurricanes = opts.hurricanes, news = opts.news || [];
  var tt = T[lang === "es" ? "es" : "en"];
  var es = lang === "es";
  var ink = brand === "sweetleaf" ? "#2A7A6F" : "#0D1B2A";
  var accent = brand === "sweetleaf" ? "#5BBCB0" : "#C8A840";
  var band = brand === "sweetleaf" ? "#E8F5F3" : "#F6F1E3";
  var h = "";
  h += '<div style="max-width:620px;margin:0 auto;font-family:Arial,Helvetica,sans-serif;color:#1a1a1a;">';
  /* 2026-10-13, Arturo: "remueve tambien los logos de sweetleaf y
     ground fresh porque ninguno se ve" — neither logo rendered in the
     actual sent email (several rounds already went into fixing the
     outline-ring artifact on each asset, to no avail once actually
     delivered) — dropped both entirely rather than keep chasing a
     third image-rendering issue; the colored band + brand name text
     alone is enough to identify the sender. */
  h += '<div style="background:' + ink + ';padding:20px 24px;border-radius:8px 8px 0 0;border-bottom:3px solid ' + accent + ';">';
  h += '<div style="color:#fff;font-size:17px;font-weight:700;">' + (es ? "Aviso Climático" : "Weather Advisory") + '</div>';
  h += '</div>';
  h += '<div style="background:#fff;border:1px solid #e2e2e2;border-top:none;padding:20px 24px;">';

  /* Hurricanes — prominent, only when something is actually active
     (Arturo: "obvio al existir huracan o alertas de huracan, esto
     afecta la cosecha, todo eso debe informarse a los clientes"). */
  if (hurricanes && hurricanes.length > 0) {
    h += '<div style="background:#FEE2E2;border:1px solid #DC2626;border-radius:6px;padding:14px 16px;margin-bottom:18px;">';
    h += '<div style="font-size:12px;font-weight:700;color:#DC2626;text-transform:uppercase;letter-spacing:0.04em;margin-bottom:8px;">' + tt.hurricaneTitle + '</div>';
    hurricanes.forEach(function (s) {
      h += '<div style="font-size:13px;color:#1a1a1a;margin-bottom:4px;"><strong>' + s.name + ' — ' + s.classification + '</strong> · ' + Math.round(s.distanceKm) + ' km ' + (es ? "de Los Reyes de Salgado" : "from Los Reyes de Salgado") + (s.maxWindsKt != null ? ' · ' + tt.maxWinds + ' ' + Math.round(s.maxWindsKt) + ' kt' : '') + '</div>';
    });
    h += '<div style="font-size:12px;color:#991B1B;margin-top:6px;">' + (es ? "Un huracán activo en el Pacífico mexicano puede afectar la cosecha y los tiempos de embarque en las regiones productoras." : "An active hurricane in the Mexican Pacific may affect harvest and shipping timelines in the producing regions.") + '</div>';
    h += '</div>';
  } else {
    h += '<div style="background:' + band + ';border-radius:6px;padding:10px 16px;margin-bottom:18px;font-size:12.5px;color:' + ink + ';font-weight:600;">✓ ' + tt.noActive + '</div>';
  }

  /* Los Reyes de Salgado, Michoacán — the main production zone, full
     current + 5-day summary (2026-10-13, Arturo: "agrega un resumen
     del forecast de los reyes michoacan qu es la zona de produccion"). */
  var mich = regionData.michoacan;
  if (mich) {
    h += '<div style="margin-bottom:16px;">';
    h += '<div style="font-size:13px;font-weight:700;color:' + ink + ';margin-bottom:8px;">' + (es ? "Los Reyes de Salgado, Michoacán" : "Los Reyes de Salgado, Michoacán") + ' <span style="font-weight:400;color:#666;">— ' + (es ? "principal zona de producción" : "main production area") + '</span></div>';
    h += '<div style="display:flex;align-items:center;gap:12px;margin-bottom:8px;">';
    h += '<img src="https://openweathermap.org/img/wn/' + mich.current.icon + '@2x.png" alt="" width="50" height="50" />';
    h += '<div><span style="font-size:22px;font-weight:700;">' + Math.round(mich.current.temp) + '°C</span> <span style="font-size:12px;color:#666;text-transform:capitalize;">' + mich.current.description + '</span></div>';
    h += '</div>';
    if (mich.alerts && mich.alerts.length > 0) {
      h += '<div style="margin-bottom:8px;">' + mich.alerts.map(function (a) { return '<span style="display:inline-block;padding:2px 9px;border-radius:9px;font-size:10.5px;font-weight:700;background:#FEE2E2;color:#DC2626;margin-right:6px;">' + (tt[ALERT_LABEL_KEY[a]] || a) + '</span>'; }).join('') + '</div>';
    }
    h += '<table style="width:100%;border-collapse:collapse;font-size:11.5px;"><tr>';
    (mich.forecast || []).forEach(function (d) {
      h += '<td style="text-align:center;padding:4px;">' + '<div style="color:#666;">' + dayLabel(d.dateKey, lang) + '</div>' + '<img src="https://openweathermap.org/img/wn/' + d.icon + '.png" width="28" height="28" alt="" />' + '<div style="font-weight:600;">' + Math.round(d.max) + '°/' + Math.round(d.min) + '°</div>' + '</td>';
    });
    h += '</tr></table>';
    h += '</div>';
  }

  /* Jalisco / Guerrero — compact one-liners, not a full dump (this is
     a summary). */
  ["jalisco", "guerrero"].forEach(function (key) {
    var rd = regionData[key];
    if (!rd) return;
    var region = REGIONS.find(function (r) { return r.key === key; });
    var name = es ? region.nameEs : region.nameEn;
    var alertsTxt = (rd.alerts || []).map(function (a) { return tt[ALERT_LABEL_KEY[a]] || a; }).join(" · ");
    h += '<div style="font-size:12.5px;padding:6px 0;border-top:1px solid #eee;">';
    h += '<strong>' + name + ':</strong> ' + Math.round(rd.current.temp) + '°C, ' + rd.current.description;
    if (alertsTxt) h += ' — <span style="color:#DC2626;font-weight:700;">' + alertsTxt + '</span>';
    h += '</div>';
  });

  /* News — real linked headlines, best-effort. */
  if (news.length > 0) {
    h += '<div style="margin-top:18px;padding-top:14px;border-top:1px solid #e2e2e2;">';
    h += '<div style="font-size:11px;font-weight:700;color:' + ink + ';text-transform:uppercase;letter-spacing:0.04em;margin-bottom:8px;">' + (es ? "Noticias relacionadas" : "Related headlines") + '</div>';
    news.forEach(function (n) {
      h += '<div style="font-size:12.5px;margin-bottom:6px;"><a href="' + n.url + '" style="color:' + ink + ';text-decoration:none;font-weight:600;">' + n.title + '</a>' + (n.source ? ' <span style="color:#999;">— ' + n.source + '</span>' : '') + '</div>';
    });
    h += '</div>';
  }

  /* 2026-10-12, Arturo: "Generated by GFI Nexus... eso no es
     relevante" — footer dropped, no replacement. */
  h += '</div></div>';
  return h;
}

/* 2026-10-13, Arturo: "el forecast repite un dia, el lunes" — real bug,
   confirmed with a real screenshot (Mon, Mon, Tue, Wed, Thu, each with
   different data — not a render glitch, two genuinely different day
   buckets). Root cause: this used to take the bucket's representative
   raw epoch (`dt`, the first 3-hour block grouped into that UTC
   calendar day) and format it with the VIEWER's local timezone — every
   day after the first starts its bucket exactly at 00:00 UTC, which
   in any timezone behind UTC (the US, Mexico) falls back onto the
   PREVIOUS local calendar day. That shifted every single day's label
   back by one, so day 2's real Tuesday showed as "Mon" (colliding
   with day 1), day 3's real Wednesday showed as "Tue", etc. — not just
   a cosmetic duplicate, every label after the first was wrong.
   Fixed by formatting the bucket's own UTC date KEY (set once at
   grouping time, never re-derived from a shiftable epoch) and forcing
   UTC in the formatter too, so the label always matches the grouping
   regardless of where the browser's clock is set. */
function dayLabel(dateKey, lang) {
  return new Date(dateKey + "T12:00:00Z").toLocaleDateString(lang === "es" ? "es-MX" : "en-US", { weekday: "short", timeZone: "UTC" });
}

/* Groups OpenWeather's 3-hour /forecast list into daily buckets: min/max
   temp, the icon closest to midday, and summed rain (for the 24h
   heavy-rain check on day 0 only). */
/* 2026-10-13, Arturo: "en la app del clima de mi iphone veo lluvia en
   los reyes los proximos [5] dias y nexus no muestra lluvia" — two
   real gaps found, both fixed here:
   1) The display icon only ever came from the single 3-hour block
      closest to local midday — a day with real rain in the morning or
      evening but a dry midday reading showed a sunny/cloudy icon,
      hiding rain this screen's own downloaded data already has.
      Now tracks the block with the highest `pop` (probability of
      precipitation, OpenWeather's own per-block field — the same
      signal a phone weather app bases its "chance of rain" on) and
      uses THAT block's icon whenever the day looks rainy at all
      (pop >= 0.4), falling back to the old midday-nearest icon
      otherwise so a genuinely dry day doesn't get a misleading icon.
   2) Nothing tracked `pop` or a per-day rain total at all — only the
      cumulative next-24h mm total existed, checked by the "rain"
      alert elsewhere. maxPop and rainMm are now returned per day so a
      caller can flag ANY of the 5 available days as rainy, not only
      the next 24 hours (see the "rain" alert / Send Weather Alert
      wiring below, which previously only ever looked at index 0). */
function groupForecastByDay(list) {
  var byDay = {};
  (list || []).forEach(function (item) {
    var dateKey = new Date(item.dt * 1000).toISOString().substring(0, 10);
    if (!byDay[dateKey]) byDay[dateKey] = { dateKey: dateKey, dt: item.dt, min: item.main.temp_min, max: item.main.temp_max, icon: item.weather[0].icon, rain3h: [], hour: new Date(item.dt * 1000).getUTCHours(), maxPop: 0, rainMm: 0, popIconHour: null };
    var d = byDay[dateKey];
    d.min = Math.min(d.min, item.main.temp_min);
    d.max = Math.max(d.max, item.main.temp_max);
    var rainAmt = (item.rain && item.rain["3h"]) || 0;
    d.rain3h.push(rainAmt);
    d.rainMm += rainAmt;
    var pop = typeof item.pop === "number" ? item.pop : 0;
    var hour = new Date(item.dt * 1000).getUTCHours();
    if (pop > d.maxPop) { d.maxPop = pop; d.popIconHour = { icon: item.weather[0].icon, hour: hour }; }
    /* Prefer the entry closest to local midday for the display icon,
       UNLESS the day looks rainy (maxPop >= 0.4) — then prefer the
       icon from whichever block actually has the highest chance of
       rain, applied once per day after the full day's blocks are in
       (see below), not per-item here. */
    if (Math.abs(hour - 12) < Math.abs(d.hour - 12)) { d.icon = item.weather[0].icon; d.hour = hour; }
  });
  var days = Object.keys(byDay).sort().slice(0, 5).map(function (k) { return byDay[k]; });
  days.forEach(function (d) {
    if (d.maxPop >= 0.4 && d.popIconHour) d.icon = d.popIconHour.icon;
    delete d.popIconHour;
  });
  return days;
}

/* Great-circle distance in km between two lat/lon points. */
function haversineKm(lat1, lon1, lat2, lon2) {
  var R = 6371;
  var toRad = function (d) { return d * Math.PI / 180; };
  var dLat = toRad(lat2 - lat1);
  var dLon = toRad(lon2 - lon1);
  var a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/* NHC's CurrentStorms.json field shapes vary by field (plain signed
   numbers for some, "19.5N"/"102.5W" hemisphere-suffixed strings for
   others depending on feed version) — this wasn't verified against a
   live response (no active storm to test against at build time), so
   handle both forms defensively rather than assume one. */
function parseCoordValue(raw) {
  if (typeof raw === "number") return raw;
  if (typeof raw !== "string") return NaN;
  var s = raw.trim();
  var m = s.match(/^(-?\d+(\.\d+)?)\s*([NSEW])?$/i);
  if (!m) return parseFloat(s);
  var v = parseFloat(m[1]);
  var hemi = (m[3] || "").toUpperCase();
  if (hemi === "S" || hemi === "W") v = -Math.abs(v);
  else if (hemi === "N" || hemi === "E") v = Math.abs(v);
  return v;
}

/* Saffir-Simpson-ish classification from max sustained wind. NHC's
   `intensity` field is in knots; converted to mph for the standard
   category thresholds. */
function classifyStorm(intensityKt) {
  var kt = parseFloat(intensityKt);
  if (isNaN(kt)) return "—";
  var mph = kt * 1.15078;
  if (mph < 39) return "TD";
  if (mph < 74) return "TS";
  if (mph < 96) return "HU-1";
  if (mph < 111) return "HU-2";
  if (mph < 130) return "HU-3";
  if (mph < 157) return "HU-4";
  return "HU-5";
}

function riskColor(distanceKm) {
  if (distanceKm < 200) return { bg: "rgba(220,38,38,0.10)", text: "#DC2626", border: "rgba(220,38,38,0.3)" };
  if (distanceKm < 500) return { bg: "rgba(217,119,6,0.10)", text: "#D97706", border: "rgba(217,119,6,0.3)" };
  return { bg: "rgba(22,163,74,0.10)", text: "#16a34a", border: "rgba(22,163,74,0.3)" };
}

/* NHC CurrentStorms.json → normalized storm list, filtered to Eastern
   Pacific (EP) + Central Pacific (CP) basins (the ones that can reach
   the Mexican Pacific coast). Every field access below is guarded —
   an unexpected/changed NHC shape degrades to skipping that storm
   rather than crashing the whole card (same lesson as the Currency &
   Fuel crash fix: never trust an external response shape blindly). */
function parseNhcStorms(data) {
  var raw = (data && (data.activeStorms || data.storms)) || [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map(function (s) {
      /* 2026-10-13, Arturo: "me dice que no hay huracanes activos en
         el pacifico mexicano pero hay 3" — real bug, not a quiet day:
         CurrentStorms.json has no `basin` field at all (verified
         live) — the basin is the first 2 letters of `binNumber`
         ("EP5" -> "EP"). Every storm was silently dropped here,
         always, since `s.basin` was always undefined. */
      var basin = String(s.basin || (s.binNumber ? String(s.binNumber).slice(0, 2) : "")).toUpperCase();
      if (basin !== "EP" && basin !== "CP") return null;
      /* NHC also already provides pre-parsed numeric coordinates
         (latitudeNumeric/longitudeNumeric) — prefer those over
         re-parsing the "20.1N"/"106.4W" display strings. */
      var lat = typeof s.latitudeNumeric === "number" ? s.latitudeNumeric : parseCoordValue(s.latitude);
      var lon = typeof s.longitudeNumeric === "number" ? s.longitudeNumeric : parseCoordValue(s.longitude != null ? s.longitude : s.longitudeE);
      if (isNaN(lat) || isNaN(lon)) return null;
      var distanceKm = haversineKm(LOS_REYES.lat, LOS_REYES.lon, lat, lon);
      return {
        name: s.name || s.stormName || "—",
        classification: classifyStorm(s.intensity),
        lat: lat, lon: lon,
        maxWindsKt: parseFloat(s.intensity) || null,
        movementDir: s.movementDir != null ? s.movementDir : null,
        movementSpeedKt: s.movementSpeed != null ? parseFloat(s.movementSpeed) : null,
        distanceKm: distanceKm,
      };
    })
    .filter(Boolean)
    .sort(function (a, b) { return a.distanceKm - b.distanceKm; });
}

function HurricaneAlerts({ lang, storms, loading, error, onRetry, updatedAt }) {
  var t = T[lang === "es" ? "es" : "en"];
  return (
    <div style={{ background: "var(--color-bg-card)", border: "1px solid var(--color-border)", borderRadius: 10, padding: 16, marginBottom: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>{t.hurricaneTitle}</div>
      {loading && !storms && <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{t.refreshing}</div>}
      {!loading && error && (
        <div>
          <div style={{ fontSize: 12, color: "#DC2626", marginBottom: 8 }}>{t.hurricaneLoadError}</div>
          <button type="button" onClick={onRetry} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.retry}</button>
        </div>
      )}
      {!loading && !error && storms && storms.length === 0 && (
        <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.25)", color: "#16a34a", fontSize: 12.5, fontWeight: 600 }}>
          ✓ {t.noActive}
        </div>
      )}
      {!loading && !error && storms && storms.length > 0 && storms.map(function (storm, i) {
        var rc = riskColor(storm.distanceKm);
        return (
          <div key={i} style={{ padding: "10px 12px", borderRadius: 8, background: rc.bg, border: "1px solid " + rc.border, marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: rc.text }}>{storm.name} — {storm.classification}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: rc.text }}>{Math.round(storm.distanceKm)} km</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 11, color: "var(--color-text-secondary)" }}>
              <div>{t.maxWinds}: <strong>{storm.maxWindsKt != null ? Math.round(storm.maxWindsKt) + " kt" : "—"}</strong></div>
              <div>{t.movement}: <strong>{storm.movementDir != null ? storm.movementDir + "° / " + (storm.movementSpeedKt != null ? Math.round(storm.movementSpeedKt) + " kt" : "—") : "—"}</strong></div>
              <div style={{ gridColumn: "1 / -1" }}>{storm.lat.toFixed(1)}°, {storm.lon.toFixed(1)}°</div>
            </div>
          </div>
        );
      })}
      {updatedAt && <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 4 }}>{t.updated}: {updatedAt}</div>}
    </div>
  );
}

function RegionCard({ region, lang, data, loading, error, onRetry }) {
  var t = T[lang === "es" ? "es" : "en"];
  var name = lang === "es" ? region.nameEs : region.nameEn;
  var note = lang === "es" ? region.noteEs : region.noteEn;

  return (
    <div style={{ background: "var(--color-bg-card)", border: "1px solid var(--color-border)", borderRadius: 10, padding: 16, marginBottom: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: note ? 2 : 8 }}>{name}</div>
      {note && <div style={{ fontSize: 10.5, color: "var(--color-text-muted)", fontStyle: "italic", marginBottom: 8 }}>{note}</div>}

      {loading && <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{t.refreshing}</div>}
      {!loading && error && (
        <div>
          <div style={{ fontSize: 12, color: "#DC2626", marginBottom: 8 }}>{t.loadError}</div>
          <button type="button" onClick={onRetry} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.retry}</button>
        </div>
      )}
      {!loading && !error && data && (
        <>
          {data.alerts.length > 0 && (
            <div style={{ marginBottom: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
              {data.alerts.map(function (a, i) {
                return <span key={i} style={{ padding: "3px 9px", borderRadius: 10, fontSize: 10, fontWeight: 700, background: "rgba(220,38,38,0.10)", color: "#DC2626", border: "1px solid rgba(220,38,38,0.3)" }}>{t[ALERT_LABEL_KEY[a]] || a}</span>;
              })}
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
            <img src={"https://openweathermap.org/img/wn/" + data.current.icon + "@2x.png"} alt="" width={56} height={56} />
            <div>
              <div style={{ fontSize: 28, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>{Math.round(data.current.temp)}°C</div>
              <div style={{ fontSize: 11, color: "var(--color-text-muted)", textTransform: "capitalize" }}>{data.current.description}</div>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, fontSize: 11, marginBottom: 12 }}>
            <div><span style={{ color: "var(--color-text-muted)" }}>{t.feelsLike}</span><div style={{ fontWeight: 600 }}>{Math.round(data.current.feelsLike)}°C</div></div>
            <div><span style={{ color: "var(--color-text-muted)" }}>{t.humidity}</span><div style={{ fontWeight: 600 }}>{data.current.humidity}%</div></div>
            <div><span style={{ color: "var(--color-text-muted)" }}>{t.wind}</span><div style={{ fontWeight: 600 }}>{Math.round(data.current.windKmh)} km/h</div></div>
          </div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>{t.forecast5}</div>
          <div style={{ display: "flex", gap: 10, overflowX: "auto" }}>
            {data.forecast.map(function (d, i) {
              return (
                <div key={i} style={{ textAlign: "center", minWidth: 46 }}>
                  <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{dayLabel(d.dateKey, lang)}</div>
                  <img src={"https://openweathermap.org/img/wn/" + d.icon + ".png"} alt="" width={32} height={32} />
                  <div style={{ fontSize: 10, fontWeight: 600 }}>{Math.round(d.max)}° / {Math.round(d.min)}°</div>
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 10 }}>{t.updated}: {data.updatedAt}</div>
        </>
      )}
    </div>
  );
}

export default function WeatherWatchRegistry({ lang: globalLang }) {
  var lang = globalLang || "es";
  var t = T[lang === "es" ? "es" : "en"];
  var [apiCfg, setApiCfg] = useState(null);
  var [loadingCfg, setLoadingCfg] = useState(true);
  var [regionData, setRegionData] = useState({});
  var [regionLoading, setRegionLoading] = useState({});
  var [regionError, setRegionError] = useState({});
  var [hurricanes, setHurricanes] = useState(null);
  var [hurricaneLoading, setHurricaneLoading] = useState(false);
  var [hurricaneError, setHurricaneError] = useState(false);
  var [hurricaneUpdatedAt, setHurricaneUpdatedAt] = useState("");
  /* 2026-10-13, Arturo: "el boton refresh de weather watch no hace
     nada" — not actually broken, just silent: once a region already
     has data, RegionCard's own loading flag (`regionLoading[key] &&
     !regionData[key]`) never shows the "Actualizando…" state again,
     so a manual refresh fetched fresh data with zero visible feedback
     unless the numbers happened to change. The button itself now
     tracks its own busy state and relabels/disables while in flight. */
  var [refreshingAll, setRefreshingAll] = useState(false);
  var aliveRef = useRef(true);

  /* Send Weather Alert — see the big comment above buildWeatherAlertHtml.
     Two steps: (1) a small picker (audience + which companies), (2) the
     real EmailDialog, pre-built with the right language/account/
     recipients/body for that one audience. Three audiences, not a
     single mixed send, because language AND From-account both change
     per audience — EmailDialog sends one body to one recipient list
     from one account, so each audience is its own dialog launch. */
  var [alertPickerOpen, setAlertPickerOpen] = useState(false);
  var [alertAudience, setAlertAudience] = useState("shippers");
  var [alertCompanyIds, setAlertCompanyIds] = useState([]);
  var [alertBuilding, setAlertBuilding] = useState(false);
  var [alertEmailData, setAlertEmailData] = useState(null);
  var openAlertPicker = function () {
    setAlertAudience("shippers");
    setAlertCompanyIds([]);
    setAlertPickerOpen(true);
  };
  var alertAudienceOptions = useCallback(function () {
    if (alertAudience === "fourSeasons") {
      return getClients().filter(function (c) { return c && c.isActive !== false && isFourSeasonsClientName(c.shortName || c.fullName || ""); })
        .map(function (c) { return { value: c.id, label: c.shortName || c.fullName }; });
    }
    if (alertAudience === "clients") {
      /* "SweetLeaf Produce" is GFI's own brand name for Four Seasons
         sales, not a real external client (CLAUDE.md: "SweetLeaf is
         GFI's brand for sales to Four Seasons") — excluded here same
         as Four Seasons itself, so it never shows up as a mailable
         company under "Clients". */
      return getClients().filter(function (c) {
        var name = c.shortName || c.fullName || "";
        return c && c.isActive !== false && !isFourSeasonsClientName(name) && name.toLowerCase().indexOf("sweetleaf") < 0;
      }).map(function (c) { return { value: c.id, label: c.shortName || c.fullName }; });
    }
    return getSuppliers().filter(function (s) { return s && s.isActive !== false; }).map(function (s) { return { value: s.id, label: s.shortName || s.fullName }; });
  }, [alertAudience]);
  /* Shared by buildAlertRecipients and the greeting builder below — one
     place resolving which real catalog records are selected. */
  var pickedAlertCompanies = function () {
    var pool = alertAudience === "fourSeasons" || alertAudience === "clients" ? getClients() : getSuppliers();
    return pool.filter(function (c) { return alertCompanyIds.indexOf(String(c.id)) >= 0; });
  };
  var buildAlertRecipients = function () {
    var picked = pickedAlertCompanies();
    var out = [];
    picked.forEach(function (c) {
      /* 2026-10-12, Arturo: "al enviar el email a twin river solo me
         muestra victor y gabe, necesito me muestre todos" — clients
         keep their real, full recipient roster in distributionList
         (used everywhere else a client email goes out — Sales Report,
         Protection Status Reminder, etc.), NOT in the sparse
         `contacts` array (Twin River: 2 contacts vs. 10 real recipients
         in distributionList). Same fallback convention as the rest of
         the app: prefer distributionList when the client has one,
         fall back to contacts only if it doesn't. Suppliers never have
         a distributionList (confirmed against real data — Gomez
         Berries: 4 contacts, 0 distributionList) so they're untouched.
         All start checked — Arturo picks who to exclude, per contact,
         same as before. */
      var roster = (c.distributionList && c.distributionList.length > 0) ? c.distributionList : (c.contacts || []);
      roster.forEach(function (ct) {
        if (!ct || !ct.email) return;
        var name = ct.name || [ct.firstName, ct.lastName].filter(Boolean).join(" ") || [ct.nombre, ct.apPaterno].filter(Boolean).join(" ") || "";
        out.push({ name: (name ? name + " · " : "") + (c.shortName || c.fullName || ""), email: ct.email, checked: true });
      });
    });
    return out;
  };
  var handleAlertContinue = async function () {
    if (alertCompanyIds.length === 0) { alert(T[lang === "es" ? "es" : "en"].alertNoCompanies); return; }
    setAlertBuilding(true);
    try {
      var isShippers = alertAudience === "shippers";
      var isFS = alertAudience === "fourSeasons";
      var alertLang = isShippers ? "es" : "en";
      var tt = T[alertLang];
      var news = await fetchAlertNews();
      var recipients = buildAlertRecipients();
      var subject = tt.alertSubject;
      var bodyHtml = buildWeatherAlertHtml({ lang: alertLang, brand: isFS ? "sweetleaf" : "gfi", regionData: regionData, hurricanes: hurricanes, news: news });
      /* 2026-10-12, Arturo: "Dear Partners... sigueme otra palabra que
         no sea partners tanto para clientes como para shippers" →
         "mas bien usar el nombre corto del cliente o shipper y
         agregar Team" — e.g. "Four Seasons Team,"/"Twin River Team,".
         Built from the real selected catalog records (same ones
         buildAlertRecipients resolves), not a generic salutation.
         Multiple companies selected at once join as "X Team, Y Team,".
         Falls back to the old generic greeting only if a company
         somehow has no name on file. */
      var greetNames = pickedAlertCompanies().map(function (c) { return c.shortName || c.fullName || ""; }).filter(Boolean);
      /* 2026-10-12, Arturo: "Dear Partners... sigueme otra palabra que
         no sea partners tanto para clientes como para shippers" →
         "mas bien usar el nombre corto del cliente o shipper y
         agregar Team" — e.g. "Four Seasons Team,"/"Twin River Team,".
         Multiple companies selected at once join as "X Team, Y Team,".
         Falls back to a generic greeting only if a company somehow
         has no name on file. Shippers additionally get a leading
         "⚠️ AVISO IMPORTANTE —" per the quality-warning redesign below
         — approved verbatim by Arturo. */
      var greeting = greetNames.length > 0
        ? greetNames.map(function (n) { return n + " Team"; }).join(", ") + ","
        : (alertLang === "es" ? "Estimados," : "Dear partners,");
      if (isShippers) greeting = "⚠️ AVISO IMPORTANTE — " + greeting;
      /* 2026-10-12, Arturo — two rounds on the default body text:
         (1) "este mensaje me parece muy poco... es una alerta de un
         huracan afectando la zona, la produccion se podria ver
         afectada, esto podria afectar las proximas ordenes... agrega
         ese contexto" → "sin huracan no enviaria alerta, solo si hay
         lluvias en el forecast de los reyes" — the body reflects the
         REAL reason the alert exists: an active hurricane (named)
         takes priority, otherwise heavy rain in the Los Reyes de
         Salgado forecast (the same >50mm/24h threshold already
         driving that region's own "rain" badge, never a separate
         invented one); falls back to a plain line only if Arturo
         opens this dialog without either condition being true.
         (2) "al shipper el mensaje debe ser distinto... enfocarse en
         vigilar el clima, y sobre todo apretar la calidad, cosechar
         fruta mojada conlleva un riesgo muy alto" — shippers get an
         entirely separate, quality-focused wording (their own harvest
         practice is what's at risk, not GFI's sell-side orders),
         clients/Four Seasons keep the original wording (production/
         order impact on the business relationship). Both share the
         same hurricane/rain detection, just different framing. */
      var nearestHurricane = (hurricanes && hurricanes.length > 0) ? hurricanes[0] : null;
      var michAlerts = (regionData.michoacan && regionData.michoacan.alerts) || [];
      var hasRainAlert = michAlerts.indexOf("rain") >= 0;
      var rainMm = (regionData.michoacan && regionData.michoacan.forecastRainMm) || 0;
      var rainEs = rainIntensityWord(rainMm, "es");
      var rainEn = rainIntensityWord(rainMm, "en");
      /* 2026-10-12, Arturo: "si esta el huracan y ves lluvias en el
         forecast en lugar de decir que podria traer lluvias di que ya
         trajo lluvias en la region" — when BOTH conditions are true at
         once, the rain is no longer hypothetical (it's the same real
         forecast data already driving the "rain" badge), so the
         wording switches from conditional ("podría traer") to factual
         ("ya ha traído"/"has already brought"). Hurricane-only (no
         rain yet) and rain-only (no hurricane) keep their own,
         separate conditional wording below. */
      var contextLine;
      if (isShippers) {
        if (nearestHurricane && hasRainAlert) {
          contextLine = "Les pedimos vigilar de cerca las condiciones climáticas: el huracán " + nearestHurricane.name + " se encuentra activo en el Pacífico mexicano y ya ha traído lluvias " + rainEs + " a la zona de producción.\n\nEs indispensable extremar las precauciones de calidad en la cosecha. Cosechar fruta mojada representa un riesgo muy alto de hongos y pudrición — los problemas de calidad son serios y afectan directamente los retornos.\n\nPor favor mantengan comunicación constante sobre las condiciones en campo.";
        } else if (nearestHurricane) {
          /* 2026-10-12, Arturo: "debes hacer referencia ya sea al
             huracan o a lluvias en el forecast o a ambos, todo es de
             acuerdo a lo que el forecast anuncia" — even below the
             50mm "rain" alert threshold, if the forecast shows any
             rain at all, name its real intensity instead of the vague
             "ante cualquier lluvia o humedad" hedge; only falls back
             to that hedge when the forecast is genuinely dry. */
          var rainLeadEs = rainMm > 0
            ? ("El pronóstico también muestra lluvias " + rainEs + " en la zona, por lo que es indispensable")
            : "Ante cualquier cambio en las condiciones climáticas, es indispensable";
          contextLine = "Les pedimos vigilar de cerca las condiciones climáticas: el huracán " + nearestHurricane.name + " se encuentra activo en el Pacífico mexicano y podría afectar la zona de producción en los próximos días.\n\n" + rainLeadEs + " extremar las precauciones de calidad en la cosecha. Cosechar fruta mojada representa un riesgo muy alto de hongos y pudrición — los problemas de calidad son serios y afectan directamente los retornos.\n\nPor favor mantengan comunicación constante sobre las condiciones en campo.";
        } else if (hasRainAlert) {
          contextLine = "El pronóstico para Los Reyes de Salgado, Michoacán muestra lluvias " + rainEs + " en los próximos días. Les pedimos vigilar de cerca las condiciones climáticas en su zona de producción.\n\nEs indispensable extremar las precauciones de calidad en la cosecha: cosechar fruta mojada representa un riesgo muy alto de hongos y pudrición — los problemas de calidad son serios y afectan directamente los retornos.";
        } else {
          contextLine = "Compartimos el siguiente aviso climático para la zona de producción. Les pedimos estar atentos a las condiciones de campo y mantener los estándares de calidad en la cosecha.";
        }
      } else if (nearestHurricane && hasRainAlert) {
        contextLine = alertLang === "es"
          ? "Estamos monitoreando de cerca el huracán " + nearestHurricane.name + ", actualmente activo en el Pacífico mexicano, que ya ha traído lluvias " + rainEs + " a nuestra zona de producción. Esto podría afectar la actividad de cosecha y las próximas órdenes. Les mantendremos informados conforme evolucione la situación.\n\nDetalle completo a continuación."
          : "We are closely monitoring Hurricane " + nearestHurricane.name + ", currently active in the Mexican Pacific, which has already brought " + rainEn + " rain to our production zone. This may affect harvest activity and could impact upcoming orders. We will keep you updated as the situation develops.\n\nFull details below.";
      } else if (nearestHurricane) {
        contextLine = alertLang === "es"
          ? "Estamos monitoreando de cerca el huracán " + nearestHurricane.name + ", actualmente activo en el Pacífico mexicano, que podría afectar las condiciones en nuestra zona de producción. Dependiendo de su trayectoria e intensidad, esto podría impactar la actividad de cosecha y las próximas órdenes. Les mantendremos informados conforme evolucione la situación.\n\nDetalle completo a continuación."
          : "We are closely monitoring Hurricane " + nearestHurricane.name + ", currently active in the Mexican Pacific, which may affect conditions across our production zone. Depending on its track and intensity, this could impact harvest activity and may affect upcoming orders. We will keep you updated as the situation develops.\n\nFull details below.";
      } else if (hasRainAlert) {
        contextLine = alertLang === "es"
          ? "El pronóstico para Los Reyes de Salgado, Michoacán — nuestra principal zona de producción — muestra lluvias " + rainEs + " en los próximos días. Esto podría afectar la actividad de cosecha y las próximas órdenes. Les mantendremos informados conforme evolucionen las condiciones.\n\nDetalle completo a continuación."
          : "Our forecast for Los Reyes de Salgado, Michoacán — our main production zone — shows " + rainEn + " rain expected in the coming days. This may affect harvest activity and could impact upcoming orders. We will keep you updated as conditions develop.\n\nFull details below.";
      } else {
        contextLine = alertLang === "es"
          ? "Compartimos el siguiente aviso climático para las regiones productoras de berries."
          : "Please find below our latest weather advisory for the berry production regions.";
      }
      /* 2026-10-13, Arturo: "la firma si bien no se agrega mi firma,
         debe informar la empresa, Ground-Fresh International para
         ground fresh y Sweetleaf Produce para sweetleaf esto despues
         de Saludos," — no personal signature (confirmed earlier,
         signatures stays []), but the company name still needs to
         sign off the message so the recipient knows which GFI brand
         is writing. */
      var signOffCompany = isFS ? "Sweetleaf Produce" : "Ground-Fresh International";
      var defaultBody = greeting + "\n\n" + contextLine + "\n\n" + (alertLang === "es" ? "Saludos," : "Best regards,") + "\n\n" + signOffCompany;
      if (isFS) {
        var slAccts = await resolveSlAlertAccounts();
        setAlertEmailData({
          lang: alertLang, recipients: recipients, subject: subject, defaultBody: defaultBody, bodyHtml: bodyHtml,
          /* fromEmail is matched against emailAccounts[].email (the
             REAL address), not maskedFrom — EmailDialog resolves the
             maskedFrom alias itself at send/preview time from the
             matched account. Same convention as every other
             automated-account send in this app. 2026-10-13, real
             production incident — EmailDialog's handleSend does NOT
             just trust this prop at send time: unless
             refreshAccountsBeforeSend is passed, it refetches
             company_profile itself and re-matches there — the WRONG
             table for this account (sweetleaf_settings), which
             silently sent from Arturo's raw personal address with no
             mask at all. resolveSlAlertAccounts is the exact same
             resolver used above, passed again here so that refetch
             never runs. */
          fromEmail: slAccts[0] ? slAccts[0].email : "", emailAccounts: slAccts,
          refreshAccountsBeforeSend: resolveSlAlertAccounts,
          signatures: [], defaultSignatureId: undefined, company: null,
        });
      } else {
        /* 2026-10-12, Arturo: "ground-fresh de alerts@gfinexus.com...
           no quiero que vaya mi firma solo el logo de la empresa que
           lo envia" — same dedicated "GFI Automated" account (and
           empty signature list) already used for the FOB warehouse
           send (ShipmentRegistry.js) and the client Protection Status
           Reminder (InvoiceRegistry.js), never Arturo's own personal
           Ground-Fresh masked account/signature.
           2026-10-13, real production incident — same root cause as
           the SweetLeaf branch, worse here: "GFI Automated" shares its
           real .email with the separate personal "Ground-Fresh"
           account in company_profile, so EmailDialog's default
           refetch-and-match-by-email at send time silently picked
           whichever one happens to come first in that array (the
           personal one) — overriding what this screen already
           resolved correctly for display. refreshAccountsBeforeSend
           below makes EmailDialog skip that refetch entirely and
           reuse this exact resolution instead. */
        var gfAccts = await resolveGfAlertAccounts();
        setAlertEmailData({
          lang: alertLang, recipients: recipients, subject: subject, defaultBody: defaultBody, bodyHtml: bodyHtml,
          fromEmail: gfAccts[0] ? gfAccts[0].email : "", emailAccounts: gfAccts,
          refreshAccountsBeforeSend: resolveGfAlertAccounts,
          signatures: [], defaultSignatureId: undefined, company: null,
        });
      }
      setAlertPickerOpen(false);
    } finally {
      setAlertBuilding(false);
    }
  };

  useEffect(function () {
    aliveRef.current = true;
    loadApiSetting("openweather").then(function (cfg) { if (aliveRef.current) { setApiCfg(cfg); setLoadingCfg(false); } });
    return function () { aliveRef.current = false; };
  }, []);

  /* NHC CurrentStorms.json — public feed, no api_settings row needed
     (fix-dashboard.md point 4: "la alerta se muestra siempre, sin
     requerir configuración de API key"). Routed through the proxy's
     mode:"fetch" — see api-proxy.js's comment on why, even though
     this specific feed's CORS support was never verified live. */
  var fetchHurricanes = useCallback(async function () {
    setHurricaneLoading(true); setHurricaneError(false);
    try {
      var res = await fetch("/.netlify/functions/api-proxy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ apiName: "nhc_hurricanes", mode: "fetch" }),
      });
      var payload = await res.json();
      if (!payload.ok) throw new Error(payload.error || "NHC request failed");
      var data = JSON.parse(payload.body);
      var storms = parseNhcStorms(data);
      if (!aliveRef.current) return;
      setHurricanes(storms);
      setHurricaneUpdatedAt(new Date().toLocaleTimeString(lang === "es" ? "es-MX" : "en-US", { hour: "2-digit", minute: "2-digit" }));
    } catch (e) {
      if (aliveRef.current) setHurricaneError(true);
    } finally {
      if (aliveRef.current) setHurricaneLoading(false);
    }
  }, [lang]);

  useEffect(function () {
    fetchHurricanes();
    var id = setInterval(fetchHurricanes, HURRICANE_REFRESH_MS);
    return function () { clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  var fetchRegion = useCallback(async function (region, cfg) {
    setRegionLoading(function (p) { return { ...p, [region.key]: true }; });
    setRegionError(function (p) { return { ...p, [region.key]: false }; });
    try {
      /* Routed through the Netlify proxy (fix-dashboard.md "Bug CORS
         al llamar Frankfurter ... asegurar que todas las llamadas API
         externas pasen por el proxy"). The proxy appends &appid=...
         itself, so this path omits it. */
      var qs = "lat=" + region.lat + "&lon=" + region.lon + "&units=metric";
      var results = await Promise.all([
        fetchViaProxy("openweather", "/weather?" + qs, { apiKey: cfg.apiKey, endpointUrl: cfg.endpointUrl }),
        fetchViaProxy("openweather", "/forecast?" + qs, { apiKey: cfg.apiKey, endpointUrl: cfg.endpointUrl }),
      ]);
      var cur = results[0], fc = results[1];

      var windKmh = (cur.wind && cur.wind.speed ? cur.wind.speed : 0) * 3.6;
      var forecastDays = groupForecastByDay(fc.list);

      /* Alert KEYS, not pre-translated strings (2026-10-13) — the Send
         Weather Alert email needs to render these in the RECIPIENT's
         language (Spanish for shippers, English for clients),
         independent of whatever language the app's UI toggle happens
         to be in when the data was fetched. ALERT_LABEL_KEY below maps
         each key to its T[...] label for both the on-screen card and
         the email. */
      var alerts = [];
      if (forecastDays.length > 0 && forecastDays[0].min < 2) alerts.push("frost");
      if (windKmh > 60) alerts.push("wind");
      /* 2026-10-13, Arturo: "en la app del clima de mi iphone veo
         lluvia en los reyes los proximos 5 dias y nexus no muestra
         lluvia" — this used to only check next24hRain > 50mm (today
         only, and a very high bar — a genuinely heavy-rain-day
         total). A phone weather app's "rain in the forecast" is
         closer to "meaningful chance of rain on any of the available
         days" — scan all 5 days this screen actually has, flag a day
         rainy at >=40% chance of precipitation (OpenWeather's own
         `pop` field) OR a modest real rain total (>1mm), not just an
         extreme 24h total. rainyDays stays empty (no alert) on a
         genuinely dry forecast. */
      var rainyDays = forecastDays.filter(function (d) { return d.maxPop >= 0.4 || d.rainMm > 1; });
      if (rainyDays.length > 0) alerts.push("rain");
      /* Representative rain amount for the email's intensity wording
         (rainIntensityWord) — the heaviest of the rainy days found,
         not strictly "today". */
      var forecastRainMm = rainyDays.length > 0 ? Math.max.apply(null, rainyDays.map(function (d) { return d.rainMm; })) : 0;

      if (!aliveRef.current) return;
      setRegionData(function (p) {
        return {
          ...p,
          [region.key]: {
            current: {
              temp: cur.main.temp, feelsLike: cur.main.feels_like, humidity: cur.main.humidity,
              windKmh: windKmh, icon: cur.weather[0].icon, description: cur.weather[0].description,
            },
            forecast: forecastDays,
            alerts: alerts,
            /* Rain total (mm) of the heaviest rainy day found across
               the full 5-day window (not just next24h — see rainyDays
               above), kept alongside the boolean "rain" alert flag so
               callers that need actual intensity wording (e.g. the
               Send Weather Alert email) can classify it themselves. */
            forecastRainMm: forecastRainMm,
            updatedAt: new Date().toLocaleTimeString(lang === "es" ? "es-MX" : "en-US", { hour: "2-digit", minute: "2-digit" }),
          },
        };
      });
    } catch (e) {
      if (aliveRef.current) setRegionError(function (p) { return { ...p, [region.key]: true }; });
    } finally {
      if (aliveRef.current) setRegionLoading(function (p) { return { ...p, [region.key]: false }; });
    }
  }, [lang, t]);

  var fetchAll = useCallback(function (cfg) {
    return Promise.all(REGIONS.map(function (r) { return fetchRegion(r, cfg); }));
  }, [fetchRegion]);

  useEffect(function () {
    if (!apiCfg || !apiCfg.configured) return;
    fetchAll(apiCfg);
    var id = setInterval(function () { fetchAll(apiCfg); }, AUTO_REFRESH_MS);
    return function () { clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiCfg]);

  if (loadingCfg) return null;

  return (
    <div style={{ maxHeight: "calc(100vh - 120px)", overflowY: "auto", paddingRight: 4 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>{t.title}</h2>
          <p style={{ fontSize: 11, color: "var(--color-text-muted)", margin: 0 }}>{t.subtitle}</p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {apiCfg && apiCfg.configured && (
            <button type="button" disabled={refreshingAll} onClick={function () {
              setRefreshingAll(true);
              Promise.all([fetchAll(apiCfg), fetchHurricanes()]).finally(function () { if (aliveRef.current) setRefreshingAll(false); });
            }} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: refreshingAll ? "var(--color-text-muted)" : "var(--color-text-secondary)", cursor: refreshingAll ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 600 }}>{refreshingAll ? t.refreshing : t.refresh}</button>
          )}
          <button type="button" onClick={function () { openAlertPicker(); }} style={{ padding: "7px 14px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>{"✉ " + t.sendAlert}</button>
        </div>
      </div>

      <HurricaneAlerts
        lang={lang}
        storms={hurricanes}
        loading={hurricaneLoading && !hurricanes}
        error={hurricaneError}
        onRetry={fetchHurricanes}
        updatedAt={hurricaneUpdatedAt}
      />

      {(!apiCfg || !apiCfg.configured) ? (
        <ApiNotConfiguredNotice apiDisplayName={t.notConfigured} lang={lang} />
      ) : (
        REGIONS.map(function (region) {
          return (
            <RegionCard
              key={region.key}
              region={region}
              lang={lang}
              data={regionData[region.key]}
              loading={!!regionLoading[region.key] && !regionData[region.key]}
              error={!!regionError[region.key]}
              onRetry={function () { fetchRegion(region, apiCfg); }}
            />
          );
        })
      )}

      {alertPickerOpen && (
        <DraggableModal title={t.sendAlert} onClose={function () { setAlertPickerOpen(false); }} zIndex={10001}>
          <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-muted)", marginBottom: 4 }}>{t.alertAudience}</div>
              <SearchSelect
                value={alertAudience}
                onChange={function (v) { setAlertAudience(v); setAlertCompanyIds([]); }}
                options={[
                  { value: "shippers", label: t.alertShippers },
                  { value: "clients", label: t.alertClients },
                  { value: "fourSeasons", label: t.alertFourSeasons },
                ]}
              />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-muted)", marginBottom: 4 }}>{t.alertCompanies}</div>
              <div style={{ fontSize: 10.5, color: "var(--color-text-muted)", fontStyle: "italic", marginBottom: 6 }}>{t.alertCompaniesHint}</div>
              <SearchSelect
                value={alertCompanyIds}
                onChange={function (v) { setAlertCompanyIds(v); }}
                multi
                options={alertAudienceOptions().map(function (o) { return { value: String(o.value), label: o.label }; })}
              />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6 }}>
              <button type="button" onClick={function () { setAlertPickerOpen(false); }} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.alertCancel}</button>
              <button type="button" disabled={alertBuilding} onClick={handleAlertContinue} style={{ padding: "8px 18px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "#fff", cursor: alertBuilding ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700 }}>{alertBuilding ? t.alertBuilding : t.alertContinue}</button>
            </div>
          </div>
        </DraggableModal>
      )}

      {alertEmailData && (
        <EmailDialog
          lang={alertEmailData.lang}
          title={t.sendAlert}
          recipients={alertEmailData.recipients}
          docOptions={[]}
          defaultSubject={alertEmailData.subject}
          defaultBody={alertEmailData.defaultBody}
          fromEmail={alertEmailData.fromEmail}
          emailAccounts={alertEmailData.emailAccounts}
          /* 2026-10-13, real production incident — this was set on the
             alertEmailData object (handleAlertContinue) but never
             actually wired to the JSX prop here, so EmailDialog's
             handleSend never saw it, silently fell back to its
             default company_profile refetch-and-match-by-email at
             send time, and picked the wrong account (confirmed by
             Arturo: dialog correctly showed "SL Automated" from the
             fromEmail/emailAccounts props below, but the real send
             went out as a.gonzalez.sweetleaf@gfinexus.com instead of
             Sweetleaf-Alerts@gfinexus.com). */
          refreshAccountsBeforeSend={alertEmailData.refreshAccountsBeforeSend}
          signatures={alertEmailData.signatures}
          defaultSignatureId={alertEmailData.defaultSignatureId}
          insertBeforeSignatureHtml={alertEmailData.bodyHtml}
          onClose={function () { setAlertEmailData(null); }}
          zIndex={10002}
        />
      )}
    </div>
  );
}
